export type BuildInfoResponse = {
  buildId?: unknown;
  version?: unknown;
  commit?: unknown;
  builtAt?: unknown;
  /** T-0329: the oldest client contract this deployment still serves. See `client-contract.json`. */
  clientContract?: unknown;
};

/** Why a reload happens: a newer build taken at a safe moment, or a client the server has outgrown. */
export type DeploymentReloadReasonType = 'update' | 'incompatible';

export type DeploymentReloadMonitorOptions = {
  initialBuildId?: string | null;
  buildInfoUrl?: string;
  intervalMs?: number;
  fetchBuildInfo?: typeof fetch;
  /**
   * T-0329: the contract this bundle was built against (`CLIENT_CONTRACT`). A deployment serving a
   * higher one no longer serves this client. `null` never compares as outgrown.
   */
  clientContract?: number | null;
  reload?: (reason: DeploymentReloadReasonType) => void;
  shouldDeferReload?: () => boolean;
  /** A newer build is waiting; `incompatible` when the server has outgrown this client (T-0329). */
  onReloadDeferred?: (buildId: string, incompatible: boolean) => void;
  /**
   * I-0593 RS-2: the checks keep failing — said on the third failure in a row, once per run of them,
   * with why; `null` once when a check succeeds again. One failure is a redeploy blip and says nothing.
   */
  onCheckFailing?: (reason: string | null) => void;
  documentRef?: Pick<Document, 'addEventListener' | 'removeEventListener' | 'visibilityState'>;
  setIntervalRef?: typeof window.setInterval;
  clearIntervalRef?: typeof window.clearInterval;
  now?: () => number;
};

const DEFAULT_BUILD_INFO_URL = '/build-info.json';
const DEFAULT_INTERVAL_MS = 30_000;
/** I-0593 RS-2: how many failed checks in a row are a fault rather than a redeploy blip. */
export const FAILED_CHECKS_BEFORE_REPORTING = 3;

/** A check that did not get a build: why, in words a log reader can act on. */
class BuildInfoUnreadable extends Error {
  constructor(readonly reason: string) {
    super(reason);
  }
}

/** RS-3: JSON, or a `+json` type; read from the header, before the body. */
function isJsonContentType(contentType: string | null): boolean {
  return contentType !== null && /^application\/([\w.+-]*\+)?json\b/i.test(contentType.trim());
}

/** What asking for the waiting build did. */
export type DeploymentReloadOutcomeType = 'reloaded' | 'blocked-by-drafts' | 'nothing-pending';

/** The served contract, or `null` when the deployment names none (one older than T-0329). */
export function getClientContract(buildInfo: BuildInfoResponse): number | null {
  const value = buildInfo.clientContract;
  return typeof value === 'number' && Number.isInteger(value) ? value : null;
}

export function getBuildId(buildInfo: BuildInfoResponse): string | null {
  const candidate = buildInfo.buildId ?? buildInfo.version ?? buildInfo.commit ?? buildInfo.builtAt;
  return typeof candidate === 'string' && candidate.trim().length > 0 ? candidate : null;
}

function cacheBustedUrl(url: string, now: () => number) {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}t=${now()}`;
}

/**
 * Picks up a new deployment without taking the screen away from the person reading it.
 *
 * ⛔ **I-0781: a check never reloads the screen a reader is on.** It used to: within 30 s of any
 * deploy that changed `/build-info.json`, `check` called `location.reload()`, and the reload reopened
 * the same address at its top. Local stable is redeployed several times an hour, so a reader halfway
 * down a decision's context was thrown back to its start "after a while" — Jay, 2026-09-27, measured
 * as a decision pane going from `scrollTop` 12098 to 0 in the built bundle. It runs only in a
 * production build, which is why no dev-server check ever saw it.
 *
 * So a new build found while the reader is on a screen waits as `pendingBuildId`, and is taken at the
 * next moment the screen is being replaced anyway: `reloadIfPending`, which the app calls when the
 * reader navigates. The one check allowed to reload at once is the first, made as the app starts —
 * nothing has been read yet, and a tab that woke up on an old bundle should not wait for a click.
 * Unsaved drafts still defer either reload, as before.
 */
export function createDeploymentReloadMonitor(options: DeploymentReloadMonitorOptions = {}) {
  const buildInfoUrl = options.buildInfoUrl ?? DEFAULT_BUILD_INFO_URL;
  const intervalMs = options.intervalMs ?? DEFAULT_INTERVAL_MS;
  const fetchBuildInfo = options.fetchBuildInfo ?? window.fetch.bind(window);
  const reload = options.reload ?? (() => window.location.reload());
  const clientContract = options.clientContract ?? null;
  const shouldDeferReload = options.shouldDeferReload ?? (() => false);
  const onReloadDeferred = options.onReloadDeferred ?? (() => undefined);
  const onCheckFailing = options.onCheckFailing ?? (() => undefined);
  const documentRef = options.documentRef ?? document;
  const setIntervalRef = options.setIntervalRef ?? window.setInterval.bind(window);
  const clearIntervalRef = options.clearIntervalRef ?? window.clearInterval.bind(window);
  const now = options.now ?? Date.now;

  let currentBuildId: string | null = options.initialBuildId ?? null;
  let pendingBuildId: string | null = null;
  let intervalId: number | undefined;
  let isReloading = false;
  /**
   * The first check, made as the app starts: nothing on the screen has been read yet. Taken by that
   * check alone, when it starts — a later check that answers before it (gate 630: under load the
   * interval's check overtook the start-up one) is a check under a screen being read (I-0781).
   */
  let startingUp = true;
  /** T-0329: the deployment serves a higher contract than this bundle's. Never goes back. */
  let incompatible = false;
  /** I-0593 RS-2: failed checks in a row, and whether this run of them has been said. */
  let failedChecks = 0;
  let failureReported = false;

  function checkFailed(reason: string) {
    failedChecks += 1;
    if (failedChecks >= FAILED_CHECKS_BEFORE_REPORTING && !failureReported) {
      failureReported = true;
      onCheckFailing(reason);
    }
  }

  function checkSucceeded() {
    failedChecks = 0;
    if (failureReported) {
      failureReported = false;
      onCheckFailing(null);
    }
  }

  function reloadNow() {
    isReloading = true;
    reload(incompatible ? 'incompatible' : 'update');
  }

  /** What `onReloadDeferred` last said, so it speaks once per build and once more if it turns out outgrown. */
  let announced: { buildId: string; incompatible: boolean } | null = null;

  function remember(nextBuildId: string) {
    pendingBuildId = nextBuildId;
    if (announced?.buildId === nextBuildId && announced.incompatible === incompatible) return;
    announced = { buildId: nextBuildId, incompatible };
    onReloadDeferred(nextBuildId, incompatible);
  }

  function reloadOrDefer(nextBuildId: string, atStart: boolean) {
    // ⛔ T-0329: a client the server has outgrown does not wait for the reader's next move — its
    // requests may already be failing. Only an unsaved draft holds it, and only until it is saved.
    if ((!atStart && !incompatible) || shouldDeferReload()) {
      remember(nextBuildId);
      return;
    }
    reloadNow();
  }

  async function check() {
    if (isReloading) {
      return;
    }

    const atStart = startingUp;
    startingUp = false;
    await checkBuild(atStart);
  }

  /**
   * The next build, taken now: the screen is being replaced anyway (I-0781), or the reader asked for
   * it. Says what happened, so the one who asked can tell them when an unsaved draft is in the way.
   */
  function reloadIfPending(): DeploymentReloadOutcomeType {
    if (isReloading) return 'reloaded';
    if (!pendingBuildId || pendingBuildId === currentBuildId) return 'nothing-pending';
    if (shouldDeferReload()) return 'blocked-by-drafts';
    reloadNow();
    return 'reloaded';
  }

  /** T-0329: an outgrown client reloads the moment the draft that held it is saved or discarded. */
  function reloadIfIncompatible(): DeploymentReloadOutcomeType {
    if (!incompatible) return 'nothing-pending';
    return reloadIfPending();
  }

  /**
   * The served build, or a refusal saying why there is none (I-0593 RS-1, RS-3). A 200 is not enough:
   * an origin without the file answers with `index.html` and a 200 (nginx `try_files … /index.html`,
   * Vite's fallback), and that used to throw inside `response.json()` and be swallowed as a blip,
   * which stopped every reload for good. The type is read before the body, so `index.html` is never
   * parsed.
   */
  async function readServedBuild(): Promise<{ served: BuildInfoResponse; buildId: string }> {
    let response: Response;
    try {
      response = await fetchBuildInfo(cacheBustedUrl(buildInfoUrl, now), {
        cache: 'no-store',
        credentials: 'same-origin',
      });
    } catch {
      // The browser already logged the request's own failure; this says what it cost.
      throw new BuildInfoUnreadable('got no answer');
    }
    if (!response.ok) throw new BuildInfoUnreadable(`answered ${response.status}`);
    const contentType = response.headers?.get('content-type') ?? null;
    if (!isJsonContentType(contentType)) {
      throw new BuildInfoUnreadable(`served as ${contentType ?? 'no content type'}, not JSON`);
    }
    let served: BuildInfoResponse;
    try {
      served = (await response.json()) as BuildInfoResponse;
    } catch {
      throw new BuildInfoUnreadable('served a body that is not JSON');
    }
    const buildId = served && typeof served === 'object' ? getBuildId(served) : null;
    if (!buildId) throw new BuildInfoUnreadable('named no build id');
    return { served, buildId };
  }

  async function checkBuild(atStart: boolean) {
    let served: BuildInfoResponse;
    let nextBuildId: string;
    try {
      ({ served, buildId: nextBuildId } = await readServedBuild());
    } catch (error) {
      // One of these is a redeploy interrupting static serving for a moment: the stale app stays
      // usable and the next check retries. A run of them is a fault, and is said (RS-2).
      checkFailed(
        `${buildInfoUrl} ${error instanceof BuildInfoUnreadable ? error.reason : 'could not be read'}`,
      );
      return;
    }
    checkSucceeded();

    const servedContract = getClientContract(served);
    if (clientContract !== null && servedContract !== null && servedContract > clientContract) {
      incompatible = true;
      reloadOrDefer(nextBuildId, atStart);
      return;
    }

    if (currentBuildId !== null && nextBuildId !== currentBuildId) {
      reloadOrDefer(nextBuildId, atStart);
      return;
    }

    // A build another check found waits for the reader's next move, whichever check answers last.
    if (pendingBuildId && pendingBuildId !== currentBuildId) {
      reloadOrDefer(pendingBuildId, false);
      return;
    }

    currentBuildId = nextBuildId;
  }

  function handleVisibilityChange() {
    if (documentRef.visibilityState === 'visible') {
      void check();
    }
  }

  function start() {
    void check();
    intervalId = setIntervalRef(() => {
      void check();
    }, intervalMs);
    documentRef.addEventListener('visibilitychange', handleVisibilityChange);

    return stop;
  }

  function stop() {
    if (intervalId !== undefined) {
      clearIntervalRef(intervalId);
      intervalId = undefined;
    }
    documentRef.removeEventListener('visibilitychange', handleVisibilityChange);
  }

  return {
    check,
    reloadIfPending,
    reloadIfIncompatible,
    isIncompatible: () => incompatible,
    start,
    stop,
    getCurrentBuildId: () => currentBuildId,
    getPendingBuildId: () => pendingBuildId,
  };
}
